import { HttpClient, HttpParams } from '@angular/common/http';
import { computed, inject, Injectable, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import { BehaviorSubject, catchError, distinctUntilChanged, EMPTY, map, Observable, of, switchMap, tap } from 'rxjs';

import { environment } from '~environments/environment.development';
import type { OptionalGetStationsQueryParam, Station, StationsFilters } from '~models/station.model';
import { StationBrand } from '~models/station.model';
import { NotificationService } from '~services/notification.service';
import { TranslationService } from '~services/translation.service';
import { BusinessUnit, DEFAULT_BUSINESS_UNIT } from '~shared/business-unit';
import { getBuFromQueryParams, getCountryFromBu, getRegionFromBu } from '~utils/business-unit.utils';
import { getAcceptLanguageHeader } from '~utils/get-accept-language-header.utils';
import { sentryError } from '~utils/sentry-error';
import { isStationOpen } from '~utils/station-status.utils';

@Injectable({
  providedIn: 'root',
})
export class StationsService {
  private readonly allStations = signal<Station[]>([]);
  private readonly http = inject(HttpClient);
  private readonly notificationService = inject(NotificationService);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly translationService = inject(TranslationService);
  readonly filters = signal<StationsFilters>({ services: [], fuels: [], openNow: false, loyaltyPrograms: [] });
  readonly filtersCount = computed(() => {
    const filters = this.filters();

    return (
      filters.services.length +
      filters.fuels.length +
      (filters.openNow ? 1 : 0) +
      (filters.loyaltyPrograms?.length ?? 0)
    );
  });
  readonly filteredStations = computed(() => {
    const stations = this.allStations();

    return this.filters().openNow ? stations.filter((s) => isStationOpen(s.openingHours)) : stations;
  });
  readonly isFiltersViewOpened = signal(false);
  readonly selectedStation = signal<Station | null>(null);
  readonly selectedStationTabIndex = signal(0);
  readonly isUsFuelDay = signal(false); // This is set in fuel day resolver;
  readonly mapBounds$ = new BehaviorSubject<google.maps.LatLngBoundsLiteral | null>(null);
  private readonly fuelDaysParticipatingStationsSet = signal<Set<string>>(new Set());
  //Consider creating a service for business unit.
  private readonly bu$ = this.activatedRoute.queryParamMap.pipe(map(getBuFromQueryParams), distinctUntilChanged());
  private readonly businessUnit = toSignal(this.bu$, { initialValue: DEFAULT_BUSINESS_UNIT });
  // Request more results during US Fuel Day to account for client-side filtering.
  private readonly getStationsMaxResults = computed(() => (this.isUsFuelDay() ? 500 : 100));
  selectStation(station: Station): void {
    this.selectedStation.set(station);
  }

  clearSelectedStation(): void {
    this.selectedStation.set(null);
    this.selectedStationTabIndex.set(0);
  }

  fetchStations(): Observable<Station[]> {
    const mapBounds = this.mapBounds$.value;

    if (this.businessUnit() === BusinessUnit.Mock) {
      this.allStations.set(MOCK_STATIONS);

      return of(MOCK_STATIONS);
    }

    if (!this.hasValidMapBounds(mapBounds)) {
      return EMPTY;
    }

    const region = getRegionFromBu(this.businessUnit());
    const params = this.buildStationsSearchParams(mapBounds);
    const headers = getAcceptLanguageHeader(this.translationService.getBaseLanguage());

    return this.http
      .get<Station[]>(`${environment.apiBaseUrl}/${region}/ngrp-store-locator/v1/stations`, { params, headers })
      .pipe(
        catchError((error) => {
          sentryError(error, 'API call failed: /stations', error.url, { params });

          return EMPTY;
        }),
        map((stations) => this.applyBusinessUnitStationRules(stations)),
        tap((stations) => {
          this.allStations.set(stations);
        }),
      );
  }

  getStation(stationId: string): Observable<Station> {
    const region = getRegionFromBu(this.businessUnit());
    const headers = getAcceptLanguageHeader(this.translationService.getBaseLanguage());

    return this.http.get<Station>(`${environment.apiBaseUrl}/${region}/ngrp-store-locator/v1/stations/${stationId}`, {
      headers,
    });
  }

  getNearestStation(position: GeolocationPosition): Observable<Station> {
    const region = getRegionFromBu(this.businessUnit());
    const headers = getAcceptLanguageHeader(this.translationService.getBaseLanguage());
    const { latitude, longitude } = position.coords;

    const params = new HttpParams().set('latitude', latitude).set('longitude', longitude).set('maxResults', 1);

    return this.http
      .get<{ stations: Station[] }>(`${environment.apiBaseUrl}/${region}/ngrp-store-locator/v1/stations/nearest`, {
        params,
        headers,
      })
      .pipe(
        switchMap((response) => {
          const { stations } = response;

          if (!stations.length) {
            this.notificationService.error('locationUnavailable');

            return EMPTY;
          }

          return of(stations[0]);
        }),
        catchError((error) => {
          sentryError(
            'Find Nearest Station Error',
            'Could not find nearest station',
            'Find Nearest Station API Error',
            {
              error,
            },
          );

          this.notificationService.error('locationUnavailable');

          return EMPTY;
        }),
      );
  }

  setFuelDaysParticipatingStations(stationIds: string[]): void {
    this.fuelDaysParticipatingStationsSet.set(new Set(stationIds));
  }

  private buildStationsSearchParams(mapBounds: google.maps.LatLngBoundsLiteral): HttpParams {
    const { east, west, north, south } = mapBounds;

    const requiredParams = new HttpParams()
      .set('topLeftLatitude', north)
      .set('topLeftLongitude', west)
      .set('bottomRightLatitude', south)
      .set('bottomRightLongitude', east)
      .set('maxResults', this.getStationsMaxResults())
      .set('country', getCountryFromBu(this.businessUnit()).toLocaleUpperCase())
      .set('brand', `${StationBrand.CIRCLEK},${StationBrand.COUCHE_TARD}`);

    return this.appendOptionalParams(requiredParams);
  }

  private appendOptionalParams(searchParams: HttpParams): HttpParams {
    const { fuels, services, loyaltyPrograms } = this.filters();

    // HttpParams are immutable, so every append returns a new instance.
    const paramsWithServices = this.applyOptionalParam(searchParams, 'serviceNames', services);
    const paramsWithFuels = this.applyOptionalParam(paramsWithServices, 'fuelNames', fuels);
    const paramsWithLoyaltyPrograms = this.applyOptionalParam(paramsWithFuels, 'loyaltyPrograms', loyaltyPrograms);

    return paramsWithLoyaltyPrograms;
  }

  private applyOptionalParam(params: HttpParams, name: OptionalGetStationsQueryParam, values?: string[]): HttpParams {
    if (!values?.length) return params;

    return params.append(name, values.join(','));
  }

  private applyBusinessUnitStationRules(stations: Station[]): Station[] {
    //ONLY CA BU has specific conditions that should apply to all stations.
    if (this.businessUnit() === BusinessUnit.CA) {
      return stations.map((station) => ({ ...station, openingHours: { alwaysOpen: true } }));
    }

    // this is a temporary solution for US Fuel Days WEB-2647
    if (this.isUsFuelDay()) {
      if (this.fuelDaysParticipatingStationsSet().size === 0) {
        return stations; // No participating stations set, return all stations
      }

      return stations.filter(({ id }) => this.fuelDaysParticipatingStationsSet().has(id));
    }

    return stations;
  }

  private hasValidMapBounds(
    mapBounds: google.maps.LatLngBoundsLiteral | null,
  ): mapBounds is google.maps.LatLngBoundsLiteral {
    if (!mapBounds) return false;

    const { east, west, north, south } = mapBounds;

    //Guard against the Maps API handing back a partial literal
    if (east == null || west == null || north == null || south == null) return false;

    /* Even though Google Maps can return west > east, our backend cannot resolve this correctly.
     * This happens wherever the viewport crosses the 180th meridian (reachable for Alaska/Pacific views)
     * Our backend returns 400, so we must avoid making requests in this scenario.
     */
    if (east <= west || north <= south) return false;

    return true;
  }
}

const MOCK_STATIONS: Station[] = [
  {
    id: '1234567',
    latitude: 39.9526,
    longitude: -75.1652,
    phoneNumber: '+12155551234',
    address: {
      street: '171 Rodeo Drive',
      city: 'Philadelphia',
      postalCode: '19103',
      state: 'PA',
      country: 'US',
    },
    services: [
      { displayName: 'NA_EVCHARGING', name: 'EV Charging' },
      { displayName: 'NA_CARWASH', name: 'Car Wash' },
    ],
    fuels: [
      { displayName: 'NA_DIESEL', name: 'Diesel' },
      { displayName: 'NA_UNLEADED', name: 'Unleaded' },
    ],
    loyaltyPrograms: [{ displayName: 'INNER_CIRCLE', name: 'Inner Circle' }],
    fuelPrices: [
      {
        code: 'UNL',
        price: 3.599,
        currency: 'usd',
        volumeUnit: 'gallon',
        lastUpdated: '2025-10-15T11:34:14.331Z',
      },
    ],

    openingHours: {
      alwaysOpen: false,
      monday: { open: '09:00', close: '23:00' },
      tuesday: { open: '09:00', close: '23:00' },
      wednesday: { open: '09:00', close: '23:00' },
      thursday: { open: '09:00', close: '23:00' },
      friday: { open: '09:00', close: '23:00' },
      saturday: { open: '09:00', close: '23:00' },
      sunday: { open: '09:00', close: '23:00' },
    },
    distance: 35.5,
    travelTime: 48,
  },
  {
    id: '2345678',
    latitude: 39.955,
    longitude: -75.17,
    phoneNumber: '+12155552345',
    address: {
      street: '1500 Market Street',
      city: 'Philadelphia',
      postalCode: '19102',
      state: 'PA',
      country: 'US',
    },
    services: [{ displayName: 'NA_TRUCKSERVICES', name: 'Truck Services' }],
    fuels: [{ displayName: 'NA_DIESEL', name: 'Diesel' }],
    openingHours: {
      alwaysOpen: true,
    },
    distance: 38.2,
    travelTime: 52,
  },
  {
    id: '3456789',
    latitude: 39.948,
    longitude: -75.158,
    phoneNumber: '+12155553456',
    address: {
      street: '2000 Chestnut Street',
      city: 'Philadelphia',
      postalCode: '19103',
      state: 'PA',
      country: 'US',
    },
    services: [
      { displayName: 'NA_EVCHARGING', name: 'EV Charging' },
      { displayName: 'NA_CARWASH', name: 'Car Wash' },
      { displayName: 'NA_ATM', name: 'ATM' },
    ],
    fuels: [
      { displayName: 'NA_UNLEADED', name: 'Unleaded' },
      { displayName: 'NA_PREMIUM', name: 'Premium' },
    ],
    loyaltyPrograms: [{ displayName: 'INNER_CIRCLE', name: 'INNER_CIRCILE' }],
    openingHours: {
      alwaysOpen: false,
      monday: { open: '06:00', close: '22:00' },
      tuesday: { open: '06:00', close: '22:00' },
      wednesday: { open: '06:00', close: '22:00' },
      thursday: { open: '06:00', close: '22:00' },
      friday: { open: '06:00', close: '22:00' },
      saturday: { open: '07:00', close: '22:00' },
      sunday: { open: '07:00', close: '22:00' },
    },
    distance: 40.1,
    travelTime: 55,
  },
  {
    id: '4567890',
    latitude: 39.96,
    longitude: -75.18,
    phoneNumber: '+12155554567',
    address: {
      street: '3000 Walnut Street',
      city: 'Philadelphia',
      postalCode: '19104',
      state: 'PA',
      country: 'US',
    },
    services: [{ displayName: 'NA_CARWASH', name: 'Car Wash' }],
    fuels: [
      { displayName: 'NA_DIESEL', name: 'Diesel' },
      { displayName: 'NA_UNLEADED', name: 'Unleaded' },
      { displayName: 'NA_PREMIUM', name: 'Premium' },
    ],
    openingHours: {
      alwaysOpen: false,
      monday: { open: '08:00', close: '20:00' },
      tuesday: { open: '08:00', close: '20:00' },
      wednesday: { open: '08:00', close: '20:00' },
      thursday: { open: '08:00', close: '20:00' },
      friday: { open: '08:00', close: '20:00' },
      saturday: { open: '08:00', close: '20:00' },
      sunday: { open: '08:00', close: '20:00' },
    },
    distance: 42.3,
    travelTime: 58,
  },
  {
    id: '5678901',
    latitude: 39.94,
    longitude: -75.145,
    phoneNumber: '+12155555678',
    address: {
      street: '1234 South Street',
      city: 'Philadelphia',
      postalCode: '19147',
      state: 'PA',
      country: 'US',
    },
    services: [
      { displayName: 'NA_EVCHARGING', name: 'EV Charging' },
      { displayName: 'NA_TRUCKSERVICES', name: 'Truck Services' },
    ],
    fuels: [{ displayName: 'NA_DIESEL', name: 'Diesel' }],
    openingHours: {
      alwaysOpen: false,
      monday: { open: '10:00', close: '18:00' },
      tuesday: { open: '10:00', close: '18:00' },
      wednesday: { open: '10:00', close: '18:00' },
      thursday: { open: '10:00', close: '18:00' },
      friday: { open: '10:00', close: '18:00' },
      saturday: { open: '10:00', close: '18:00' },
      sunday: { open: '10:00', close: '18:00' },
    },
    distance: 44.7,
    travelTime: 60,
  },
  {
    id: '6789012',
    latitude: 39.935,
    longitude: -75.172,
    phoneNumber: '+12155556789',
    address: {
      street: '5000 City Avenue',
      city: 'Philadelphia',
      postalCode: '19131',
      state: 'PA',
      country: 'US',
    },
    services: [
      { displayName: 'NA_CARWASH', name: 'Car Wash' },
      { displayName: 'NA_ATM', name: 'ATM' },
    ],
    fuels: [
      { displayName: 'NA_UNLEADED', name: 'Unleaded' },
      { displayName: 'NA_PREMIUM', name: 'Premium' },
      { displayName: 'NA_DIESEL', name: 'Diesel' },
    ],
    openingHours: {
      alwaysOpen: false,
      monday: { open: '07:00', close: '23:00' },
      tuesday: { open: '07:00', close: '23:00' },
      wednesday: { open: '07:00', close: '23:00' },
      thursday: { open: '07:00', close: '23:00' },
      friday: { open: '07:00', close: '23:00' },
      saturday: { open: '08:00', close: '22:00' },
      sunday: { open: '08:00', close: '22:00' },
    },
    distance: 46.8,
    travelTime: 63,
  },
  {
    id: '7890123',
    latitude: 39.97,
    longitude: -75.16,
    phoneNumber: '+12155557890',
    address: {
      street: '7500 Broad Street',
      city: 'Philadelphia',
      postalCode: '19141',
      state: 'PA',
      country: 'US',
    },
    services: [
      { displayName: 'NA_EVCHARGING', name: 'EV Charging' },
      { displayName: 'NA_CARWASH', name: 'Car Wash' },
      { displayName: 'NA_TRUCKSERVICES', name: 'Truck Services' },
      { displayName: 'NA_ATM', name: 'ATM' },
      { displayName: 'NA_RESTROOM', name: 'Restroom' },
    ],
    fuels: [
      { displayName: 'NA_UNLEADED', name: 'Unleaded' },
      { displayName: 'NA_PREMIUM', name: 'Premium' },
    ],
    openingHours: {
      alwaysOpen: true,
    },
    distance: 48.9,
    travelTime: 65,
  },
];
